import { BigDataEventConstants } from "./constants/big-data-constants";
import { EventType } from "./constants/event-type-constants";
import { MarketingClientConstants } from "./constants/marketing-client-constants";
import { RwErrorMessage } from "./constants/rw-object.constants";
import { Attribute, BigDataHelper } from "./helpers/big-data-helper";
import { DeviceInformationHelper, DeviceType } from "./helpers/device-information-helper";
import { DOMHelper } from "./helpers/dom-helper";
import { BigData } from "./marketing-client-bigdata";
import { Capping } from "./marketing-client-capping";
import { Utilization } from "./marketing-client-utilization";
import { Checkbox } from "./utils/checkbox";
import { ErrorHandler } from "./utils/error-handler-first-rw";
import { Spinner } from "./utils/spinner";
import { Utils } from "./utils/utils";
import { requiredOrderConstants } from "./constants/order-constants";
import { AllowedURLParamTypes } from "./custom-types/custom-types";
import { httpInstance } from "./http/http";

export class OneDayPass {
  oneDayPass: HTMLElement;
  parent: HTMLElement | null;
  oneDayPassID = "nw_one_day_pass";
  formID = "one-day-pass-form";
  oneDayPassClass = ".nw_2022_odp";
  classSuffix = {
    email: "-form-email",
    emailError: "-form-email-error",
    checkbox: "-checkbox",
    createAccountError: "-form-create-account-error",
    button: "-button",
    spinner: "-form-spinner"
  };
  form: HTMLFormElement | null;
  email: HTMLInputElement | null;
  checkbox: Checkbox;
  createAccountButton: HTMLElement | null;
  createAccountError: HTMLElement | null;
  emailErrorHandler: ErrorHandler;
  spinnerCreateAccount: Spinner;
  isMobile: Boolean;
  messages = RwErrorMessage;
  promocode: string;
  offerID: string;
  isHarmonyTest: AllowedURLParamTypes = Utils.getURLParamsAsObject().enableHarmonyTest;
  path: string = this.isHarmonyTest ? "https://coreos05.wyborcza.pl:9443/" : "https://api-prenumerata.wyborcza.pl/";

  constructor(public capping: Capping, public utilizationObject: Utilization) {
    this.isMobile = DeviceInformationHelper.getDeviceType() === DeviceType.MOBILE;
  }

  init() {
    this.initializeOneDayPass();
    this.initializeForm();

    if (!this.form) return;
    this.initializeSpinner();
    this.initializeCheckbox();
    this.initializePromocode();
    this.initializeParent();
    this.bindEvents();
  }

  initializeOneDayPass(): void {
    this.oneDayPass = DOMHelper.getElementById(this.oneDayPassID) as HTMLElement;
  }

  initializeForm(): void {
    this.form = DOMHelper.getElementById(this.formID) as HTMLFormElement;

    if (!this.form) return;

    this.email = DOMHelper.querySelector(
      `${this.oneDayPassClass}${this.classSuffix.email}`,
      this.form
    ) as HTMLInputElement;
    this.createAccountButton = DOMHelper.getElementById(`${this.formID}${this.classSuffix.button}`) as HTMLElement;

    if (!this.email) return;

    this.emailErrorHandler = new ErrorHandler(this.email);
    this.createAccountError = DOMHelper.querySelector(
      `${this.oneDayPassClass}${this.classSuffix.createAccountError}`,
      this.form
    ) as HTMLElement;
  }

  initializePromocode(): void {
    if (this.oneDayPass.dataset.promocode) this.promocode = this.oneDayPass.dataset.promocode;
  }

  initializeSpinner(): void {
    this.spinnerCreateAccount = new Spinner(`${this.oneDayPassClass}${this.classSuffix.spinner}`);
  }

  initializeCheckbox(): void {
    this.checkbox = new Checkbox(`${this.formID}${this.classSuffix.checkbox}`);
  }

  initializeParent(): void {
    this.parent =
      this.oneDayPass && this.oneDayPass.closest(`[${MarketingClientConstants.placeholderDataSetAttribute}]`);
  }

  bindEvents(): void {
    this.bindEventsToRegistrationAccount();
    this.bindEventsToEmailInput();
  }

  bindEventsToRegistrationAccount(): void {
    if (this.createAccountButton) {
      this.createAccountButton.addEventListener(EventType.CLICK, (event) => {
        event.preventDefault();
        const validateCheckboxes = this.validateCheckboxes();

        if (this.validateEmail() && validateCheckboxes) {
          this.hideRegistrationAccountError();
          this.spinnerCreateAccount.show();
          this.utilize();
          this.sendData();
        }
      });
    }
  }

  bindEventsToEmailInput(): void {
    if (this.email) {
      this.email.addEventListener(EventType.CHANGE, this.validateEmail.bind(this));
      this.email.addEventListener(EventType.INPUT, () => {
        return this.email?.value.length
          ? this.emailErrorHandler.hide()
          : this.emailErrorHandler.show(this.messages.required);
      });
    }
  }

  validateCheckboxes(): boolean {
    this.checkbox.toggle();

    return this.checkbox.isValidate();
  }

  validateEmail(): boolean {
    const value = this.email ? this.email.value : "",
      emailIsValid = Utils.validateEmail(value);

    if (!value.length) {
      return this.emailErrorHandler.show(this.messages.required);
    } else if (!emailIsValid) {
      return this.emailErrorHandler.show(this.messages.banned);
    } else {
      return this.emailErrorHandler.hide();
    }
  }

  errorRegistrationAccount(error: any) {
    error.status === 400
      ? this.emailErrorHandler.show(this.messages.accountExists)
      : this.showRegistrationAccountError();
    this.spinnerCreateAccount.hide();
  }

  hideRegistrationAccountError(): void {
    this.createAccountError?.classList.add(MarketingClientConstants.hiddenClassName);
  }

  showRegistrationAccountError(): void {
    this.createAccountError?.classList.remove(MarketingClientConstants.hiddenClassName);
    this.spinnerCreateAccount.hide();
  }

  utilize(clickID = "", onlyBigData = false): void {
    const condition = "brak";

    if (this.parent) {
      const dataToSend = this.utilizationObject.prepareDataToSend(this.parent, condition),
        attribute: Attribute = BigDataHelper.createAttribute(this.parent, true, clickID);

      if (!onlyBigData) {
        this.utilizationObject.utilize(dataToSend);
      }

      BigData.sendDataOceanEvent(BigDataEventConstants.onClick, { ...dataToSend, ...attribute });
    }
  }

  buildOfferBody(accountType: string): object {
    return {
      offerId: this.offerID,
      email: String(this.email?.value).trim(),
      promoCode: this.promocode,
      accountType: accountType,
      ...requiredOrderConstants
    };
  }

  async sendData() {
    this.spinnerCreateAccount.show();
    const data = {
      email: this.email ? String(this.email.value).trim() : ""
    };

    await httpInstance
      .get({
        url: `${this.path}accounts/validate?` + new URLSearchParams(data),
        withCredentials: true,
        headers: {
          "Content-Type": "application/stream+json",
          Accept: "*/*"
        }
      })
      .then(this.successEmailIsValid.bind(this), this.errorRegistrationAccount.bind(this));
  }

  async successEmailIsValid() {
    const data = {
      registrationEmail: this.email ? String(this.email.value).trim() : "",
      accountType: "WYBORCZA"
    };

    await httpInstance
      .post({
        url: `${this.path}accounts/register-v2`,
        data: data,
        withCredentials: true,
        headers: {
          "Content-Type": "application/json",
          Accept: "*/*"
        }
      })
      .then(this.successRegistrationAccount.bind(this), this.showRegistrationAccountError.bind(this));
  }

  async successRegistrationAccount() {
    await httpInstance
      .get({
        url: `${this.path}promo-codes/${this.promocode}/offerId`,
        withCredentials: true,
        headers: {
          "Content-Type": "application/json",
          Accept: "*/*"
        }
      })
      .then((response) => {
        this.checkIfDataOfferId(response);
      }, this.showRegistrationAccountError.bind(this));
  }

  checkIfDataOfferId(response: any) {
    if (response.data.offerId) {
      this.offerID = response.data.offerId;
      this.softLogin();
    } else {
      this.showRegistrationAccountError();
    }
  }

  async softLogin() {
    const data = {
      email: this.email ? String(this.email.value).trim() : ""
    };

    await httpInstance
      .post({
        url: `${this.path}softlogin`,
        data: data,
        withCredentials: true,
        headers: {
          "Content-Type": "application/json",
          Accept: "*/*"
        }
      })
      .then(this.getAccountData.bind(this), this.showRegistrationAccountError.bind(this));
  }

  async getAccountData() {
    await httpInstance
      .get({
        url: `${this.path}accounts`,
        withCredentials: true,
        headers: {
          "Content-Type": "application/json",
          Accept: "*/*"
        }
      })
      .then((response) => {
        this.checkIfDataAccountType(response);
      }, this.showRegistrationAccountError.bind(this));
  }

  checkIfDataAccountType(response: any) {
    response.data.accountType ? this.makeTheOrder(response.data.accountType) : this.showRegistrationAccountError();
  }

  async makeTheOrder(accountType: string) {
    const orderData = this.buildOfferBody(accountType);

    await httpInstance
      .post({
        url: `${this.path}orders`,
        data: orderData,
        withCredentials: true,
        headers: {
          "Content-Type": "application/json",
          Accept: "*/*"
        }
      })
      .then(() => {
        this.spinnerCreateAccount.hide();
        window.location.reload();
      }, this.showRegistrationAccountError.bind(this));
  }
}
